import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
// Commented out exam-sheet API related imports
// import GosvaraSheetFormModal, {
//   GosvaraSheetFormData,
// } from "../../../components/Modals/GosvaraSheetFormModal";
import { useMarkSheets } from "../../../hooks/useMarkSheets";
import { useStudents } from "../../../hooks/useStudents";
import { useSessionClass } from "../../../contexts/SessionClassContext";
import { useAuth } from "../../../hooks/useAuth";
import { combineGosvaraDtos, useGosvaraSheet } from "../../../hooks/useGosvaraSheet";
import { useMedium } from "../../../components/layout/Layout";
import EnhancedTable from "../../../components/common/EnhancedTable";
import { useTranslation } from "react-i18next";
import GosvaraTemplate from "../../../components/templates/GosvaraTemplate";
import GosvaraCategoryTemplate from "../../../components/templates/GosvaraCategoryTemplate";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ActionButtons from "../../../components/common/ActionButtons";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";

const GosvaraSheetManagement: React.FC = () => {
  // NOTE: Delete functionality sends studentId (not gosvara sheet id) because backend expects student ID
  // Gosvara sheet data: { id: 1, studentId: 3, ... } - we send studentId: 3 for deletion
  // NOTE: Form data conversion: GosvaraSheetFormData (LocalSubjectMark[]) -> GosvaraSheetData (SubjectMark[]) for backend compatibility

  const { user } = useAuth();
  const { selectedClass, selectedSession } = useSessionClass();
  const { selectedMedium } = useMedium();
  const schoolId = user?.schoolId;
  const classId = selectedClass?.id;
  const isGradeAll =
    (selectedClass as any)?.section === "__GRADE_ALL__" ||
    String((selectedClass as any)?.name || "").includes("-ALL");
  const classNameForAll = isGradeAll ? `${String((selectedClass as any)?.name || "").trim()}-ALL` : undefined;

  // Get gosvara data using the new API
  const { useAllGosvaraData, useCategoryWiseGosvara } = useGosvaraSheet();
  // Commented out exam-sheet API related mutations
  // const { addGosvaraSheetMutation, updateGosvaraSheetMutation } = useGosvaraSheet();

  // Commented out exam-sheet API related state
  // const [modalOpen, setModalOpen] = useState(false);
  // const [isEdit, setIsEdit] = useState(false);
  // const [currentSheet, setCurrentSheet] = useState<
  //   (GosvaraSheetFormData & { id?: number }) | undefined
  // >(undefined);


  // Get all gosvara data using the new GET API with path parameters for enhanced table
  const gosvaraDataQuery = useAllGosvaraData(
    schoolId ?? 0,
    selectedSession?.id ?? 0,
    selectedMedium || "HINDI", // Use selected medium or default to HINDI
    isGradeAll ? undefined : (classId ?? undefined),
    isGradeAll ? classNameForAll : undefined,
    !!(schoolId && selectedSession?.id && selectedMedium && (isGradeAll ? !!classNameForAll : !!classId))
  );

  const { t } = useTranslation();

  // For student name lookup
  const { studentsQuery } = useStudents(false, schoolId ?? undefined);

  // Get student name by id
  const getStudentName = (studentId: number) => {
    const student = (studentsQuery.data || []).find(s => s.id === studentId);
    return student ? `${student.studentName}` : studentId;
  };

  // Commented out Gosvara Report related state
  // const [goshwaraModalOpen, setGoshwaraModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedGosvaraData, setSelectedGosvaraData] = useState<any>(null);
  const [viewType, setViewType] = useState<"subject" | "category">("subject");

  // Hook for category-wise data
  const categoryPayload = {
    schoolId: schoolId ?? 0,
    classId: isGradeAll ? null : (classId ?? 0),
    className: isGradeAll ? classNameForAll : undefined,
    sessionId: selectedSession?.id ?? 0,
    marksheetType: selectedGosvaraData?.marksheetType ?? "",
    monthType: selectedGosvaraData?.monthType ?? "",
  };

  const categoryWiseQuery = useCategoryWiseGosvara(
    categoryPayload,
    selectedMedium || "HINDI",
    // Only fire when modal is open on category tab AND all required fields are populated
    viewModalOpen &&
    viewType === "category" &&
    !!categoryPayload.marksheetType &&
    !!categoryPayload.monthType &&
    categoryPayload.schoolId > 0 &&
    categoryPayload.sessionId > 0 &&
    ((categoryPayload.classId ?? 0) > 0 || !!categoryPayload.className)
  );

  // Note: Delete functionality not available for marksheets
  const handleOpenDeleteDialog = (studentId: number) => {
    alert(t('delete_functionality_not_available_gosvara'));
  };

  // Commented out Gosvara Report related functions
  // const handleOpenGoshwaraModal = () => setGoshwaraModalOpen(true);
  // const handleCloseGoshwaraModal = () => setGoshwaraModalOpen(false);

  const handleOpenViewModal = (gosvaraData: any) => {
    setSelectedGosvaraData(gosvaraData);
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedGosvaraData(null);
    setViewType("subject"); // Reset to subject view
  };

  const handleViewTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    nextView: "subject" | "category"
  ) => {
    if (nextView !== null) {
      setViewType(nextView);
    }
  };

  const columns = [
    { id: "marksheetType", label: t("gosvara_type"), minWidth: 130 },
    { id: "monthType", label: t("month_type"), minWidth: 130 },
    { id: "className", label: t("class_name"), minWidth: 130 },
    { id: "sessionName", label: t("session_name"), minWidth: 130 },
  ];

  // Use gosvara data directly from the new API response
  const allGosvaraDataRaw = gosvaraDataQuery.data || [];
  const allGosvaraData = useMemo(() => {
    if (!isGradeAll) return allGosvaraDataRaw;
    return combineGosvaraDtos(allGosvaraDataRaw as any, classNameForAll);
  }, [allGosvaraDataRaw, isGradeAll, classNameForAll]);

  // Filter gosvara data by selectedClass and selectedSession (show all marksheet types)
  const filteredGosvaraData = useMemo(() => {
    if (!allGosvaraData || allGosvaraData.length === 0) {
      return [];
    }

    const filtered = allGosvaraData.filter((gosvaraSheet: any) => {
      const schoolMatch = gosvaraSheet.schoolId === schoolId;
      const classMatch = isGradeAll ? true : (!selectedClass || gosvaraSheet.classId === selectedClass.id);

      return schoolMatch && classMatch;
    });

    return filtered;
  }, [allGosvaraData, schoolId, selectedClass?.id, selectedMedium, isGradeAll]);

  const formattedData = filteredGosvaraData.map((row: any, index: number) => {
    return {
      ...row,
      sno: index + 1,
      marksheetType: row.marksheetType || t('not_available'),
      monthType: row.monthType || t('not_available'),
      className: (isGradeAll ? classNameForAll : selectedClass?.name) || row.className || t('not_available'),
      sessionName: selectedSession?.name || t('not_available'),
    };
  });

  const renderActions = (row: any) => {
    return (
      <Button
        variant="contained"
        sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, minWidth: 80 }}
        size="small"
        onClick={() => handleOpenViewModal(row)}
      >
        {t("view")}
      </Button>
    );
  };

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 2 }}>
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              {t("gosvara_sheet_management")}
            </Typography>
            {/* Commented out Gosvara Report button */}
            {/* <Button
              variant="contained"
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
              onClick={handleOpenGoshwaraModal}
            >
              {t("gosvara_report")}
            </Button> */}
          </Box>

          {gosvaraDataQuery.isLoading && !!(schoolId && selectedSession?.id && classId && selectedMedium) && (
            <CircularProgress sx={{ display: "block", margin: "20px auto" }} />
          )}
          {gosvaraDataQuery.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {String(gosvaraDataQuery.error instanceof Error ? gosvaraDataQuery.error.message : t('an_error_occurred'))}
            </Alert>
          )}
          <EnhancedTable
            columns={columns}
            data={formattedData}
            searchFields={["marksheetType", "monthType", "className", "sessionName"]}
            actions={renderActions}
            cardProps={{
              elevation: 0,
              sx: {
                border: 'none',
                boxShadow: 'none',
                backgroundColor: 'transparent'
              }
            }}
          />
        </>
      </Paper>

      {/* Commented out exam-sheet API related form modal */}
      {/* <GosvaraSheetFormModal
        open={modalOpen}
        handleClose={handleCloseModal}
        initialData={currentSheet}
        onSubmit={handleSubmit}
        isEdit={isEdit}
      /> */}


      {/* Commented out Gosvara Report dialog */}
      {/* <Dialog
        open={false}
        onClose={() => {}}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {t("gosvara_report")}
          <IconButton onClick={() => {}} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box p={2}>
          <GosvaraTemplate
            schoolId={schoolId ?? 0}
            classId={classId ?? 0}
            marksheetType={selectedStatus}
            monthType={formattedData[0]?.monthType ?? "JANUARY"}
            gosvaraData={formattedData[0]}
          />
        </Box>
      </Dialog> */}

      {/* View Modal for Individual Gosvara Data */}
      <Dialog
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        maxWidth="xl"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            backgroundColor: 'white',
            flexShrink: 0,
            borderBottom: "1px solid #e0e0e0",
            py: 1
          }}
        >
          <Box display="flex" alignItems="center" gap={3}>
            <Typography variant="h6" fontWeight="bold">{t("gosvara_details")}</Typography>
            <ToggleButtonGroup
              value={viewType}
              exclusive
              onChange={handleViewTypeChange}
              size="small"
              color="primary"
              sx={{ bgcolor: '#f0f7ff' }}
            >
              <ToggleButton value="subject" sx={{ px: 2, fontWeight: 'bold' }}>
                {t("subject_wise")}
              </ToggleButton>
              <ToggleButton value="category" sx={{ px: 2, fontWeight: 'bold' }}>
                {t("category_wise")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <IconButton onClick={handleCloseViewModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box
          p={2}
          sx={{
            overflow: 'auto',
            flex: 1,
          }}
        >
          {selectedGosvaraData && (
            viewType === "subject" ? (
              <GosvaraTemplate
                schoolId={schoolId ?? 0}
                classId={classId ?? 0}
                marksheetType={selectedGosvaraData.marksheetType}
                monthType={selectedGosvaraData.monthType}
                gosvaraData={selectedGosvaraData}
              />
            ) : categoryWiseQuery.isLoading ? (
              <Box textAlign="center" py={10}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>{t("loading_category_data")}</Typography>
              </Box>
            ) : categoryWiseQuery.error ? (
              <Alert severity="error">{t("failed_to_load_category_data")}</Alert>
            ) : (
              <GosvaraCategoryTemplate
                schoolId={schoolId ?? 0}
                classId={classId ?? 0}
                marksheetType={selectedGosvaraData.marksheetType}
                monthType={selectedGosvaraData.monthType}
                categoryData={categoryWiseQuery.data}
              />
            )
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default GosvaraSheetManagement;
